import clsx from "clsx";
import { NextPage } from "next";
import { DM_Serif_Display, Plus_Jakarta_Sans, Poppins } from "next/font/google";
import { DefaultSeo } from "next-seo";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import Head from "next/head";
import Script from "next/script";
import dynamic from "next/dynamic";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import { SmoothScroll } from "~/components/animations/SmoothScroll";
import CookieBanner from "~/components/CookieBanner";
import HelpLauncherButton, {
  HELP_LAUNCHER_POSITION_CLASSES,
} from "~/components/Faq/ChatLauncherButton";
import ScrollButton from "~/components/ScrollButton";
import {
  COOKIE_PREFERENCES_CHANGE_EVENT,
  type CookiePreferences,
  getStoredCookiePreferences,
} from "~/lib/cookiePreferences";
import { GTM_ID, pageview } from "~/lib/gtag";
import { DEFAULT_SEO } from "~/next-seo.config";
import { ViewportProvider } from "~/providers/ViewportProvider";
import { getContactInfo } from "~/utils/contactInfo";
import "~/styles/globals.css";

const HelpWidget = dynamic(() => import("~/components/Faq/ChatBot"), {
  ssr: false,
});

const poppins = Poppins({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
  display: "swap",
});

const dmSerifDisplay = DM_Serif_Display({
  subsets: ["latin"],
  weight: ["400"],
  display: "swap",
});

const plusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
  display: "swap",
});

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);

  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [cookiePreferences, setCookiePreferences] =
    useState<CookiePreferences | null>(null);

  // We reuse the same layout to avoid unnecessary re-renders.
  // This causes the scroll position to persist between pages. We need to manually scroll to top
  // Also send navigation event to GA
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageview(url);
      ref.current?.scrollTo(0, 0);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    const syncCookiePreferences = () => {
      setCookiePreferences(getStoredCookiePreferences());
    };

    syncCookiePreferences();
    window.addEventListener(
      COOKIE_PREFERENCES_CHANGE_EVENT,
      syncCookiePreferences
    );

    return () => {
      window.removeEventListener(
        COOKIE_PREFERENCES_CHANGE_EVENT,
        syncCookiePreferences
      );
    };
  }, []);

  const baseUrl =
    (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
      /\/+$/,
      ""
    );
  const { email } = getContactInfo();
  const cleanPath = router.asPath.split("#")[0].split("?")[0];
  const canonicalPath = cleanPath === "/" ? "/" : cleanPath;
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  const organizationId = `${baseUrl}/#organization`;
  const websiteId = `${baseUrl}/#website`;
  const isProduction = process.env.NODE_ENV === "production";
  const analyticsAllowed =
    isProduction && cookiePreferences?.analytics === true;
  const marketingAllowed =
    isProduction && cookiePreferences?.marketing === true;
  const openGraph = {
    ...DEFAULT_SEO.openGraph,
    url: canonicalUrl,
  };

  return (
    <ViewportProvider>
      <div
        id="app"
        className={clsx("flex h-full flex-col overflow-auto")}
        ref={ref}
      >
        {" "}
        <ScrollButton containerRef={ref} />
        <DefaultSeo {...DEFAULT_SEO} canonical={canonicalUrl} openGraph={openGraph} />
        <Head>
          <style>{`:root { --font-poppins: ${poppins.style.fontFamily}; --font-dm-serif: ${dmSerifDisplay.style.fontFamily}; --font-jakarta: ${plusJakartaSans.style.fontFamily}; }`}</style>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "Organization",
                    "@id": organizationId,
                    name: "Inspektomat",
                    alternateName: "Inspektomat.pl",
                    url: baseUrl,
                    ...(email ? { email } : {}),
                    logo: `${baseUrl}/apple-touch-icon.png?v=3`,
                    image: `${baseUrl}/apple-touch-icon.png?v=3`,
                    description:
                      "Platforma do porównywania ofert firm wykonujących przeglądy instalacji i budynków w całej Polsce.",
                    areaServed: {
                      "@type": "Country",
                      name: "Polska",
                    },
                    knowsLanguage: "pl",
                    knowsAbout: [
                      "Przeglądy instalacji elektrycznych",
                      "Przeglądy instalacji gazowych",
                      "Przeglądy kominiarskie",
                      "Przeglądy wentylacji i klimatyzacji",
                      "Przeglądy przeciwpożarowe",
                      "Przeglądy budowlane",
                    ],
                    hasOfferCatalog: {
                      "@type": "OfferCatalog",
                      name: "Usługi dostępne w Inspektomacie",
                      itemListElement: [
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Przegląd instalacji elektrycznej",
                            description:
                              "Okresowa kontrola instalacji elektrycznej z oględzinami, pomiarami i protokołem dla domu, mieszkania lub budynku.",
                            url: `${baseUrl}/przeglad-instalacji-elektrycznej/`,
                          },
                        },
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Pomiary elektryczne",
                            description:
                              "Pomiary ochronne i eksploatacyjne instalacji elektrycznej z protokołem dla domu, mieszkania lub lokalu.",
                            url: `${baseUrl}/pomiary-elektryczne/`,
                          },
                        },
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Przegląd instalacji gazowej",
                            description:
                              "Kontrola bezpieczeństwa instalacji gazowych zgodna z normami PN-EN 437, PN-EN 15001 i PN-EN 1775, serwisowanie urządzeń gazowych.",
                            url: `${baseUrl}/przeglad-instalacji-gazowej/`,
                          },
                        },
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Przegląd systemów wykrywania pożaru",
                            description:
                              "Kontrola autonomicznych czujników dymu oraz systemów SSP/SAP w jednym miejscu.",
                            url: `${baseUrl}/przeglad-systemow-wykrywania-pozaru/`,
                          },
                        },
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Przegląd sprzętu przeciwpożarowego",
                            description:
                              "Kontrola gaśnic, hydrantów i podstawowego sprzętu przeciwpożarowego dla obiektu.",
                            url: `${baseUrl}/przeglad-sprzetu-przeciwpozarowego/`,
                          },
                        },
                        {
                          "@type": "Offer",
                          itemOffered: {
                            "@type": "Service",
                            name: "Rozdzielnia elektryczna",
                            url: `${baseUrl}/rozdzielnia-elektryczna/`,
                          },
                        },
                      ],
                    },
                  },
                  {
                    "@type": "WebSite",
                    "@id": websiteId,
                    url: baseUrl,
                    name: "Inspektomat",
                    publisher: {
                      "@id": organizationId,
                    },
                    inLanguage: "pl-PL",
                  },
                ],
              }),
            }}
          />
        </Head>
        {analyticsAllowed && (
          <Script
            id="gtag-base"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer', '${GTM_ID}');
              `,
            }}
          />
        )}
        {marketingAllowed && (
          <Script
            id="adsense-base"
            strategy="lazyOnload"
            src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7317949836493340"
            crossOrigin="anonymous"
          />
        )}
        {getLayout(<Component {...pageProps} />)}
        <ToastContainer autoClose={4000} hideProgressBar={true} />
        <div>
          {!isChatLoaded && (
            <div className={HELP_LAUNCHER_POSITION_CLASSES}>
              <HelpLauncherButton onClick={() => setIsChatLoaded(true)} />
            </div>
          )}
          {isChatLoaded && <HelpWidget startOpen />}
        </div>
        <CookieBanner />
      </div>
    </ViewportProvider>
  );
};

export default MyApp;
